import { RouteRecordRaw } from 'vue-router';

import { CAD_STATE, ZOLL_ONLINE, ZOLL_PRODUCTS } from '@/constants';
import { ROLE } from '@/entities/Roles';
import { IConnection } from '@/services/Connection';
import { IEnvironment } from '@/services/Environment';
import { pathJoin } from '@/utils/pathJoin';

export function createRoutes(environment: IEnvironment, connection: IConnection): RouteRecordRaw[] {
  return [
    {
      name: 'dashboard',
      path: '/',
      component: () => import(/* webpackChunkName: "map" */ './views/Map.vue'),
      meta: {
        layout: 'wide',
        showLoader: true,
      },
    },
    {
      path: '/dispatch',
      redirect: '/',
    },

    {
      name: 'portal',
      path: `/${ZOLL_PRODUCTS.PORTAL}`,
      component: () => import(/* webpackChunkName: "portal" */ './views/Portal.vue'),
      meta: {
        cadState: CAD_STATE.PORTAL,
        layout: 'wide',
        showLoader: true,
      },
    },

    // Provide a route for logout, which is handled by a logout navigation guard.
    <RouteRecordRaw> {
      name: 'logout',
      path: ZOLL_ONLINE.LOGOUT_PATH,
    },

    // Provide a route for portal logout, which is handled by a logout navigation guard.
    <RouteRecordRaw> {
      name: 'portal-logout',
      path: ZOLL_ONLINE.PORTAL_LOGOUT_PATH,
    },

    // Error.
    {
      name: 'error',
      path: '/error',
      component: () => import(/* webpackChunkName: "error" */ './services/router/Error.vue'),
    },

    {
      name: 'configuration',
      path: '/configuration',
      component: () => import(/* webpackChunkName: "configuration" */ './views/Configuration/Index.vue'),
      meta: {
        menus: ['secondary'],
        role: ROLE.ADMIN,
        hideLabel: true,
        icon: {
          name: 'cog',
          type: 'fas',
          size: 'lg',
        },
      },
      children: [
        {
          name: 'configuration/agencies',
          path: 'agencies',
          component: () => import(/* webpackChunkName: "configuration/agencies" */ './views/Configuration/Companies.vue'),
          redirect: connection.isHybrid ? '/configuration/companies' : undefined,
          meta: {
            cadState: CAD_STATE.STANDALONE,
          },
          children: [],
        },
        {
          name: 'configuration/companies',
          path: 'companies',
          component: () => import(/* webpackChunkName: "configuration/companies" */ './views/Configuration/Companies.vue'),
          redirect: connection.isStandalone ? '/configuration/agencies' : undefined,
          meta: {
            cadState: CAD_STATE.HYBRID,
          },
          children: [],
        },
        {
          name: 'configuration/features',
          path: 'features',
          component: () => import(/* webpackChunkName: "configuration/features" */ './views/Configuration/Features.vue'),
          meta: {
            role: ROLE.ZOLL_ADMIN,
          },
        },
        {
          name: 'configuration/debug',
          path: 'debug',
          component: () => import(/* webpackChunkName: "configuration/debug" */ './views/Configuration/Debug.vue'),
          meta: {
            // role: ROLE.ZOLL_ADMIN,
          },
        },
        {
          name: 'configuration/downloads',
          path: 'downloads',
          component: () => import(/* webpackChunkName: "configuration/downloads" */ './views/Configuration/Downloads.vue'),
          meta: {
            cadState: CAD_STATE.HYBRID,
          },
        },
        {
          name: 'sharing',
          path: '',
          redirect: pathJoin(environment.zoAdminBaseUrl, ZOLL_ONLINE.SHARING_PATH),
          meta: {
            cadState: connection.isHybrid ? CAD_STATE.HYBRID : CAD_STATE.STANDALONE,
            external: true,
            newWindow: true,
          },
        },
      ],
    },

    <RouteRecordRaw> {
      name: 'help',
      path: connection.isPortal ? '/portal/help' : '/help',
      meta: {
        icon: {
          name: 'question-circle',
          type: 'fas',
          size: 'lg',
        },
        menus: ['secondary'],
        newWindow: true,
      },
    },
    {
      name: 'not-found',
      path: '/:pathMatch(.*)*',
      component: () => import(/* webpackChunkName: "not-found" */ './services/router/NotFound.vue'),
    },
  ];
}
